import { css } from '@emotion/react';
import { useMemo, useCallback, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  MentorQuestionAnswer,
} from 'redux/actions/mentoring-program-participant-driven';
import NvFiltersBar from 'shared/components/filters/nv-filters-bar';
import { NvDropdownCheckbox, NvDropdownHeader } from 'shared/components/inputs/nv-dropdown';
import NvTruncatedTextWithTooltip from 'shared/components/nv-truncated-text-with-tooltip';
import { white } from 'styles/global_defaults/colors';
import { useSelector } from 'react-redux';
import { selectMentorsFilters } from 'redux/selectors/mentorship-program-participant-driven';
import { useFormContext } from 'react-hook-form';
import { config } from '@config/pendo.config.json';
import { doubleModalZIndex } from 'shared/components/nv-modal';

const BROWSE_MENTORS_FILTERS_MIN_HEIGHT = 55;
const MAX_FILTER_WIDTH = 300;

type BrowseMentorsFiltersProps = {
  isContentScrolled?: boolean;
};

const browseMentorsFiltersStyles = css`
  flex-shrink: 0;
  min-height: ${BROWSE_MENTORS_FILTERS_MIN_HEIGHT}px;

  &.scrolled {
    box-shadow: 1px 2px 2px 0px rgba(0, 0, 0, 0.1);
  }

  .browse-mentors-filters-bar {
    align-items: flex-start;
    background: ${white};
    border-bottom: none;

    .filters {
      flex: 1 1 0%;
    }
    .filter-menu {
      max-width: 279px;
    }
  }

  .bs4-dropdown {
    max-width: ${MAX_FILTER_WIDTH}px;
  }
`;

const sortAlphabetically = (a: string, b: string) => a.localeCompare(b);

const getFilters = (
  filters: MentorQuestionAnswer[],
  onChange: (id: number, filter: string, isChecked: boolean) => void,
  selectedFilters: Record<number, string[]>,
  pendingFilters: Record<number, string[]>,
  onToggle: (questionId: number) => (isOpen: boolean) => void
) => {
  return filters
    .filter((item) => item.searchableAnswers?.length > 0)
    .map(({ id, questionText, searchableAnswers }) => ({
      title: !selectedFilters[id]?.length
        ? questionText
        : `${questionText} (${selectedFilters[id]?.length})`,
      selected: !!selectedFilters[id]?.length,
      onToggle: onToggle(id),
      items: [
        { type: 'header', title: questionText, class: 'title text-wrap' },
        ...[...searchableAnswers]
          .sort(sortAlphabetically)
          .map((answer, index) => ({
            id: `${id}-${index}-${answer}`,
            type: 'checkbox',
            label: (
              <NvTruncatedTextWithTooltip
                text={answer}
                className="truncated-text"
                zIndex={doubleModalZIndex}
              />
            ),
            name: answer,
            class: 'filter-item',
            checked: pendingFilters[id]?.includes(answer) || false,
            onChanged: (isChecked: boolean) => {
              onChange(id, answer, isChecked);
            },
          })),
      ] as Array<NvDropdownHeader | NvDropdownCheckbox>,
    }));
};

const BrowseMentorsFilters = forwardRef(({
  isContentScrolled,
}: BrowseMentorsFiltersProps, ref) => {
  const filters = useSelector(selectMentorsFilters);
  const { watch, setValue } = useFormContext();
  const { filters: lastFilters } = watch();

  const [filtersSelection, setFiltersSelection] = useState<Record<number, string[]>>({});
  const [pendingSelection, setPendingSelection] = useState<Record<number, string[]>>({});

  useEffect(() => {
    setFiltersSelection(lastFilters || {});
    setPendingSelection(lastFilters || {});
    setValue('areFiltersSet', true, { shouldDirty: false, shouldValidate: false });
  }, [lastFilters, setValue]);

  const onChange = useCallback((question_id: number, filter: string, isChecked: boolean) => {
    setPendingSelection(prev => {
      let updated = { ...prev };
      const prevFilters = updated[question_id] || [];
      if (isChecked) {
        if (!prevFilters.includes(filter)) {
          updated[question_id] = [...prevFilters, filter];
        }
      } else {
        const filtered = prevFilters.filter((a) => a !== filter);
        if (filtered.length > 0) {
          updated[question_id] = filtered;
        } else {
          delete updated[question_id];
        }
      }
      return updated;
    });
  }, []);

  const handleDropdownToggle = useCallback((questionId: number) => (isOpen: boolean) => {
    if (!isOpen) {
      setFiltersSelection(prev => {
        const newState = { ...prev };
        if (pendingSelection[questionId] && pendingSelection[questionId].length > 0) {
          newState[questionId] = pendingSelection[questionId];
        } else {
          delete newState[questionId];
        }
        return newState;
      });
    }
  }, [pendingSelection]);

  const filtersList = useMemo(
    () => getFilters(filters, onChange, filtersSelection, pendingSelection, handleDropdownToggle),
    [filters, onChange, filtersSelection, pendingSelection, handleDropdownToggle]
  );

  const setFiltersAndFlag = useCallback((filtersObj: Record<number, string[]>) => {
    setValue('filters', filtersObj, { shouldDirty: true, shouldValidate: true });
    setValue('areFiltersSet', true, { shouldDirty: false, shouldValidate: false });
  }, [setValue]);

  const onApplyFilters = useCallback(() => {
    setFiltersAndFlag(filtersSelection);
  }, [filtersSelection, setFiltersAndFlag]);

  const onClearFilters = useCallback(() => {
    setFiltersSelection({});
    setPendingSelection({});
    setFiltersAndFlag({});
  }, [setFiltersAndFlag]);

  useImperativeHandle(ref, () => ({
    onClearFilters,
  }));

  if (filters.length === 0) {
    return null;
  }

  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.findAMentor.modal;

  return (
    <div
      css={browseMentorsFiltersStyles}
      className={`align-content-center ${isContentScrolled ? 'scrolled' : ''}`}
    >
      <NvFiltersBar
        filters={filtersList}
        hasFiltersSelectedOrApplied={Object.keys(filtersSelection).length > 0}
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
        className='browse-mentors-filters-bar justify-content-between'
        resetDataQa={dataQATag.resetFilters}
        applyFiltersDataQa={dataQATag.applyFilters}
        expandFiltersListDataQa={dataQATag.expandFiltersList}
        collapseFiltersListDataQa={dataQATag.collapseFiltersList}
      />
    </div>
  );
});

export default BrowseMentorsFilters;
